import { t, type VisitFileRow } from '@bms2/shared';
import { FileRow } from '../../components/composites/FileRow';
import { useDateFormat, useViewerZone } from '../../shell/ViewerZone';
import { fileRowOf } from './DocumentFilesPanel';
import { useFileRowModals } from './FileRowModals';
import docStyles from './DocumentPage.module.css';

/**
 * 🔒 The Service Report's Files tab (piece 29; decision 2910; T3833): the
 * signed sheet and every photo the visit's notes were added with, each drawn
 * as the one File row every Files list draws (decision 3010). The rows ride
 * the visit's own answer, read under the visit's read, so a worker the visit
 * opens to on the assignment alone (decision 5572) sees them; the tab makes no
 * read of its own.
 *
 * 🔒 The sheet's row opens the signed version, never a newer one, on the path the
 * visit's answer names: the visit's sheet route for a worker its assignment names
 * (decision 13090), the file route for anyone else. Share and the
 * pencil stand for a viewer who writes the job, and a caption edited there
 * reads on the notes at once, so a change reads the page again, which carries
 * the rows.
 *
 * 🔨 No upload stands here: a photo reaches the report through a note, under
 * its line, and a file filed on the job alone is the Work Order's.
 */
export function VisitFilesPanel({ files, sheet, photoIds, writes, apiBaseUrl = '', onChanged, onLinkAsset = null }: {
  /** The visit answer's rows: the sheet's, then each note photo's, each with the path it opens on (decision 13210); a deleted file has none. */
  files: readonly VisitFileRow[];
  /** The frozen sheet's file and the path the visit's answer names for it (decision 13090); null while the report is open. */
  sheet: { fileId: string; path: string } | null;
  /** Every photo's file, in the order the notes carry them. */
  photoIds: readonly string[];
  writes: boolean;
  apiBaseUrl?: string;
  onChanged: () => void;
  /** 🔒 Decision 13310: Link to Asset on each photo's row, handed where the viewer may link; absent or null, no row carries it. */
  onLinkAsset?: ((fileId: string) => void) | null;
}) {
  const zone = useViewerZone();
  const dateFormat = useDateFormat();
  const rowModals = useFileRowModals(apiBaseUrl, onChanged);
  if (sheet === null && photoIds.length === 0) return <p className={docStyles.muted}>{t('workOrder.visitPage.sheetOpen')}</p>;
  const held = new Map(files.map((file) => [file.id, file]));
  const row = (file: VisitFileRow, handle: string) => {
    const drawn = fileRowOf(file, { apiBaseUrl, zone, dateFormat, writes, share: rowModals.share, details: rowModals.details });
    // 🔒 Decision 13210: a photo's row opens the path its row carries, the visit's picture route for a worker its assignment names.
    const path = handle === 'sheet' && sheet ? sheet.path : file.path ?? null;
    const signed = path ? { href: `${apiBaseUrl}${path}` } : {};
    // 🔒 Decision 13310: a photo's row carries Link to Asset beside Share, as the opened picture does; the sheet's never does.
    const link = handle !== 'sheet' && onLinkAsset ? { secondButton: { label: t('serviceReport.photo.linkAsset'), onAct: () => onLinkAsset(file.id), icon: 'link' as const } } : {};
    return <FileRow key={file.id} {...drawn} {...signed} {...link} data={{ ...drawn.data, 'data-report-file': handle }} />;
  };
  const sheetFile = sheet ? held.get(sheet.fileId) ?? null : null;
  const photos = [...new Set(photoIds)].map((id) => held.get(id)).filter((file): file is VisitFileRow => file !== undefined);
  return (
    <>
      <ul className={docStyles.fileList} data-report-file-count={(sheetFile ? 1 : 0) + photos.length}>
        {sheetFile ? row(sheetFile, 'sheet') : null}
        {photos.map((file) => row(file, file.id))}
      </ul>
      {rowModals.modals}
    </>
  );
}
